import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ComboBuilder = ({ onAddCombo }) => {
  const [selectedMain, setSelectedMain] = useState('');
  const [selectedSide, setSelectedSide] = useState('');
  const [selectedDrink, setSelectedDrink] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const mainOptions = [
    { value: '', label: 'Select Main Item' },
    { value: 'chicken-tacos', label: 'Chicken Tacos (₹149)' },
    { value: 'beef-burrito', label: 'Beef Burrito (₹179)' },
    { value: 'veggie-quesadilla', label: 'Veggie Quesadilla (₹139)' },
    { value: 'fish-tacos', label: 'Fish Tacos (₹169)' }
  ];

  const sideOptions = [
    { value: '', label: 'Select Side' },
    { value: 'nachos', label: 'Nachos (₹79)' },
    { value: 'mexican-rice', label: 'Mexican Rice (₹59)' },
    { value: 'refried-beans', label: 'Refried Beans (₹49)' },
    { value: 'guacamole', label: 'Guacamole (₹69)' }
  ];

  const drinkOptions = [
    { value: '', label: 'Select Drink' },
    { value: 'horchata', label: 'Horchata (₹49)' },
    { value: 'jamaica', label: 'Jamaica (₹49)' },
    { value: 'mexican-coke', label: 'Mexican Coke (₹39)' },
    { value: 'agua-fresca', label: 'Agua Fresca (₹59)' }
  ];

  const calculateTotal = () => {
    const prices = {
      'chicken-tacos': 149,
      'beef-burrito': 179,
      'veggie-quesadilla': 139,
      'fish-tacos': 169,
      'nachos': 79,
      'mexican-rice': 59,
      'refried-beans': 49,
      'guacamole': 69,
      'horchata': 49,
      'jamaica': 49,
      'mexican-coke': 39,
      'agua-fresca': 59
    };

    const total = (prices?.[selectedMain] || 0) + (prices?.[selectedSide] || 0) + (prices?.[selectedDrink] || 0);
    const discount = total > 0 ? Math.round(total * 0.15) : 0;
    return { total, discount, final: total - discount };
  };

  const handleBuildCombo = () => {
    if (selectedMain && selectedSide && selectedDrink) {
      const combo = {
        main: selectedMain,
        side: selectedSide,
        drink: selectedDrink,
        ...calculateTotal()
      };
      onAddCombo(combo);
      setSelectedMain('');
      setSelectedSide('');
      setSelectedDrink('');
      setIsOpen(false);
    }
  };

  const { total, discount, final } = calculateTotal();
  const isComplete = selectedMain && selectedSide && selectedDrink;

  return (
    <div className="bg-gradient-to-br from-[var(--color-primary)]/10 to-[var(--color-accent)]/10 border-2 border-[var(--color-primary)] rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 flex items-center justify-between hover:bg-[var(--color-primary)]/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[var(--color-primary)] flex items-center justify-center shadow-lg">
            <Icon name="Utensils" size={24} color="#FFFFFF" />
          </div>
          <div className="text-left">
            <h3 className="text-xl font-bold text-[var(--color-foreground)]">Build Your Combo</h3>
            <p className="text-sm text-[var(--color-muted-foreground)]">Save 15% on combo meals</p>
          </div>
        </div>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={24} color="var(--color-primary)" />
      </button>

      {isOpen && (
        <div className="p-6 pt-0 space-y-6 animate-fade-in">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
                <Icon name="ChefHat" size={16} color="var(--color-primary)" />
                Main Item
              </label>
              <Select
                options={mainOptions}
                value={selectedMain}
                onChange={setSelectedMain}
                placeholder="Choose your main"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
                <Icon name="Cookie" size={16} color="var(--color-primary)" />
                Side Dish
              </label>
              <Select
                options={sideOptions}
                value={selectedSide}
                onChange={setSelectedSide}
                placeholder="Choose your side"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
                <Icon name="Coffee" size={16} color="var(--color-primary)" />
                Drink
              </label>
              <Select
                options={drinkOptions}
                value={selectedDrink}
                onChange={setSelectedDrink}
                placeholder="Choose your drink"
              />
            </div>
          </div>

          {isComplete && (
            <div className="bg-white rounded-lg p-4 space-y-2 animate-slide-up">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--color-muted-foreground)]">Subtotal</span>
                <span className="font-semibold text-[var(--color-foreground)]">₹{total}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--color-success)] flex items-center gap-1">
                  <Icon name="Tag" size={14} />
                  Combo Discount (15%)
                </span>
                <span className="font-semibold text-[var(--color-success)]">-₹{discount}</span>
              </div>
              <div className="pt-2 border-t border-[var(--color-border)] flex items-center justify-between">
                <span className="font-bold text-[var(--color-foreground)]">Total</span>
                <span className="text-2xl font-bold text-[var(--color-primary)]">₹{final}</span>
              </div>
            </div>
          )}

          <Button
            variant="default"
            size="lg"
            iconName="ShoppingCart"
            iconPosition="left"
            onClick={handleBuildCombo}
            disabled={!isComplete}
            fullWidth
          >
            {isComplete ? 'Add Combo to Cart' : 'Select All Items to Continue'}
          </Button>

          <div className="flex items-center justify-center gap-2 text-xs text-[var(--color-muted-foreground)]">
            <Icon name="Info" size={14} />
            <span>All combos are 100% halal and made fresh to order</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComboBuilder;